import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Mail } from 'lucide-react';

/*
 * Rechtsseiten von Immoverita.
 * Betreiber und Verantwortlicher: Dwayne Advertising (Entscheidung Alexander Fischl, 28.10.2026).
 *
 * PFLEGE-REGELN
 * - Speicherfristen (RETENTION) stehen WÖRTLICH gleich in docs/LOESCHKONZEPT.md.
 *   Frist ändern → beide Stellen + backend/retention.py im selben Commit.
 * - Neuer Dienstleister, der Daten sieht → PROCESSORS + docs/avv-liste.md + docs/VVT.md.
 * - Neuer KI-Einsatz → AI_USES (KI-Transparenz) VOR dem Live-Gang.
 * - Agentur-Standard ist eRecht24 dynamisch; bis zur Anbindung gilt dieser Text (awork-Task).
 */

export const COMPANY = {
  name: 'Dwayne Advertising – Alexander Fischl',
  product: 'Immoverita',
  rep: 'Alexander Fischl (Inhaber)',
  street: 'Innere Wiener Straße 10',
  zip: '81667',
  city: 'München',
  country: 'Deutschland',
  email: 'af@dwayne-advertising.de',
  phone: '+49 (0)89 99 2 999 88',
  vatId: 'DE306711660',
  contentResponsible: 'Alexander Fischl, Innere Wiener Straße 10, 81667 München',
};

const UPDATED = '28.10.2026';

// Wörtlich identisch mit docs/LOESCHKONZEPT.md, Abschnitt 1 (Spalte „Frist").
export const RETENTION = [
  'Konto der Hausverwaltung mit Bewertungslinks, Bewertungen und Einstellungen: bis zur Löschung des Kontos (Einstellungen → Datenschutz & AVV → „Konto löschen", Wirkung sofort).',
  'Bestätigte Konten ohne Anmeldung: 24 Monate nach dem letzten Login, nach vorheriger Ankündigung per E-Mail.',
  'Nie bestätigte Registrierungen (Login-Link nie benutzt, keine Inhalte): 30 Tage.',
  'Bewertungen samt freiwillig angegebener E-Mail-Adresse: bis zur Löschung durch den Bewerter (Link in der Bestätigungsmail), durch die Hausverwaltung oder mit dem Konto der Hausverwaltung.',
  'Login- und Bestätigungslinks: 1 Stunde (Login) bzw. 7 Tage (Bewertung bearbeiten) gültig, 7 Tage nach Ablauf gelöscht.',
  'Anwesenheitsanzeige im Bewertungsformular (zufällige Sitzungskennung, keine IP-Adresse): 1 Tag.',
  'Zahlungsereignisse von Stripe (nur Ereignis-ID und Typ): 90 Tage.',
  'Anfragen über das Kontaktformular: 12 Monate.',
  'Anwendungsprotokolle (ohne IP-Adressen der Nutzer): rollierend überschrieben, höchstens 30 MB je Dienst.',
  'Sicherheitsprotokolle des Servers (SSH-Anmeldeversuche): 5 Wochen.',
  'Sicherungskopien der Datenbank: 14 Tage, danach überschrieben.',
  'Rechnungsdaten: 10 Jahre (gesetzliche Aufbewahrungspflicht nach § 147 AO, § 14b UStG).',
];

const PROCESSORS = [
  {
    name: 'Hetzner Online GmbH',
    where: 'Gunzenhausen, Deutschland',
    what: 'Hosting von Anwendung und Datenbank. Rechenzentrum Nürnberg, alle Daten bleiben in Deutschland.',
  },
  {
    name: 'OpenAI, L.L.C.',
    where: 'San Francisco, USA',
    what: 'Textvorschläge, Sachlichkeitsprüfung und Stimmungserkennung im Bewertungsformular. Übermittelt werden nur Bewertungsinhalte (Sterne, Themen, Freitext) und der Name der Hausverwaltung, nie E-Mail-Adressen. Keine Nutzung zum Training. Grundlage: EU-US Data Privacy Framework und Standardvertragsklauseln.',
  },
  {
    name: 'Mailgun Technologies, Inc.',
    where: 'USA, Verarbeitung in der EU-Region',
    what: 'Versand von Login-Links und Bestätigungsmails zu Bewertungen, sobald der Mailversand aktiviert ist. Übermittelt werden E-Mail-Adresse und Mailinhalt. Standardvertragsklauseln für einen möglichen Supportzugriff aus den USA.',
  },
  {
    name: 'Stripe Payments Europe, Ltd.',
    where: 'Dublin, Irland',
    what: 'Zahlungsabwicklung für kostenpflichtige Pläne, sobald die Zahlungsfunktion aktiviert ist. Stripe erhält Name, E-Mail-Adresse und Zahlungsdaten der Hausverwaltung direkt; wir speichern nur Kunden- und Abo-Kennungen.',
  },
];

const AI_USES = [
  ['Textvorschlag (positiver Weg)', 'Formuliert aus Sternen und gewählten Themen einen Bewertungstext, den der Bewerter frei ändern kann', 'Name der Hausverwaltung, Sterne je Thema, optionale Stichworte', 'gpt-4o-mini'],
  ['Ausgewogener Textvorschlag', 'Sachlicher Text mit Stärken und Schwächen, wenn die Bewertung kritisch ausfällt', 'Name der Hausverwaltung, erster Sterne-Impuls, Sterne je Thema', 'gpt-4o-mini'],
  ['Text verfeinern und Titel', 'Überarbeitet den Vorschlag nach einer Anweisung des Bewerters und schlägt einen kurzen Titel vor', 'Aktueller Text, Anweisung des Bewerters', 'gpt-4o-mini'],
  ['Sachlichkeitsprüfung', 'Hinweis, ob ein kritischer Text Beleidigungen oder unbelegte Behauptungen enthält', 'Freitext der Bewertung, Sterne je Thema', 'gpt-4o-mini'],
  ['Neutral umformulieren', 'Vorschlag einer sachlicheren Fassung, nur auf Wunsch des Bewerters', 'Freitext der Bewertung, Sterne je Thema', 'gpt-4o-mini'],
  ['Stimmungserkennung', 'Ordnet eine abgeschlossene Bewertung einer Stimmung zu (z. B. „zufrieden", „verärgert") für das Dashboard der Hausverwaltung', 'Freitext der Bewertung, Durchschnitt der Sterne', 'gpt-4o-mini'],
];

const H2 = ({ children }) => <h2 className="text-base font-semibold mt-6 mb-2">{children}</h2>;
const P = ({ children }) => <p className="text-sm text-muted-foreground leading-relaxed mb-3">{children}</p>;
const UL = ({ children }) => <ul className="list-disc pl-5 space-y-1.5 text-sm text-muted-foreground leading-relaxed mb-3">{children}</ul>;
const A = ({ href, children }) => <a href={href} className="underline hover:text-foreground">{children}</a>;

function Impressum() {
  return (
    <>
      <H2>Angaben gemäß § 5 DDG</H2>
      <P>{COMPANY.name}<br />{COMPANY.street}<br />{COMPANY.zip} {COMPANY.city}<br />{COMPANY.country}</P>
      <H2>Vertreten durch</H2>
      <P>{COMPANY.rep}</P>
      <H2>Kontakt</H2>
      <P>Telefon: {COMPANY.phone}<br />E-Mail: <A href={`mailto:${COMPANY.email}`}>{COMPANY.email}</A></P>
      <H2>Umsatzsteuer-ID</H2>
      <P>Umsatzsteuer-Identifikationsnummer gemäß § 27 a UStG: {COMPANY.vatId}</P>
      <H2>Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV</H2>
      <P>{COMPANY.contentResponsible}</P>
      <H2>Verbraucherstreitbeilegung</H2>
      <P>Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.</P>
    </>
  );
}

function Datenschutz() {
  return (
    <>
      <H2>1. Verantwortlicher</H2>
      <P>{COMPANY.name}, {COMPANY.street}, {COMPANY.zip} {COMPANY.city}. E-Mail: <A href={`mailto:${COMPANY.email}`}>{COMPANY.email}</A>, Telefon: {COMPANY.phone}. Ein Datenschutzbeauftragter ist nicht bestellt, da die gesetzlichen Voraussetzungen nicht vorliegen.</P>

      <H2>2. Wer wofür verantwortlich ist</H2>
      <P><strong>Konten der Hausverwaltungen:</strong> Für die Daten der Hausverwaltungen, die Immoverita nutzen, sind wir Verantwortlicher.</P>
      <P><strong>Bewertungen:</strong> Wenn Sie als Eigentümer oder Mieter über einen Bewertungslink eine Bewertung abgeben, ist die jeweilige Hausverwaltung Verantwortlicher. Wir verarbeiten Ihre Bewertung in deren Auftrag nach Art. 28 DSGVO. Sie können sich mit Ihren Anliegen an die Hausverwaltung oder direkt an uns wenden.</P>

      <H2>3. Welche Daten wir verarbeiten</H2>
      <UL>
        <li><strong>Konto der Hausverwaltung:</strong> E-Mail-Adresse, Name der Ansprechperson, Firmendaten (Name, Anschrift, Telefon, Website, Logo, Farben), hinterlegte Links zu Bewertungsplattformen, Zeitpunkt der Registrierung, der letzten Anmeldung und der Bestätigung dieser Datenschutzerklärung.</li>
        <li><strong>Bewertung:</strong> Sterne insgesamt und je Thema, Freitext, gewählte Plattform, optionales Feedback zum Bewertungsablauf, Zeitpunkte und an welcher Stelle das Formular verlassen wurde. <strong>Freiwillig:</strong> Ihre E-Mail-Adresse und ob die Hausverwaltung Sie kontaktieren darf.</li>
        <li><strong>Kontaktformular:</strong> Name, Firma, E-Mail-Adresse, Thema und Nachricht, um Ihre Anfrage zu beantworten (Art. 6 Abs. 1 lit. b bzw. f DSGVO).</li>
        <li><strong>Technische Daten:</strong> Eine zufällige Sitzungskennung für die Live-Anzeige „gerade aktive Besucher" (keine IP-Adresse, kein Cookie). Ihre IP-Adresse nutzen wir nur kurzzeitig im Arbeitsspeicher, um Missbrauch durch zu viele Anfragen zu begrenzen. Sie wird nicht gespeichert.</li>
      </UL>

      <H2>4. Zwecke und Rechtsgrundlagen</H2>
      <UL>
        <li>Bereitstellung des Kontos und der Funktionen für Hausverwaltungen: Vertragserfüllung (Art. 6 Abs. 1 lit. b DSGVO).</li>
        <li>Erfassung und Auswertung von Bewertungen im Auftrag der Hausverwaltung: berechtigtes Interesse der Hausverwaltung an Rückmeldungen (Art. 6 Abs. 1 lit. f DSGVO).</li>
        <li>Ihre E-Mail-Adresse beim Bewerten und der Kontaktwunsch: Einwilligung (Art. 6 Abs. 1 lit. a DSGVO). Die Angabe ist freiwillig, Sie können die Einwilligung jederzeit widerrufen.</li>
        <li>Schutz vor Missbrauch und Sicherheit des Dienstes: berechtigtes Interesse (Art. 6 Abs. 1 lit. f DSGVO).</li>
      </UL>

      <H2>5. Künstliche Intelligenz</H2>
      <P>Im Bewertungsformular erhalten Sie auf Wunsch Textvorschläge und Hinweise zur Sachlichkeit. Dafür übermitteln wir Bewertungsinhalte an OpenAI. Sterne werden von der KI nie verändert, Sie entscheiden selbst, ob Sie einen Vorschlag übernehmen. Einzelheiten stehen auf der Seite <Link to="/ki-transparenz" className="underline hover:text-foreground">KI-Transparenz</Link>.</P>

      <H2>6. Empfänger und Auftragsverarbeiter</H2>
      <P>Mit allen Dienstleistern, die Daten sehen können, bestehen Verträge zur Auftragsverarbeitung nach Art. 28 DSGVO:</P>
      <UL>
        {PROCESSORS.map((p) => (
          <li key={p.name}><strong>{p.name}</strong> ({p.where}): {p.what}</li>
        ))}
      </UL>
      <P>Die Hausverwaltung, für die Sie eine Bewertung abgeben, sieht Ihre Bewertung und, falls angegeben, Ihre E-Mail-Adresse. Veröffentlichen Sie Ihren Text selbst auf Google, Trustpilot oder ProvenExpert, gelten dort deren Datenschutzbestimmungen.</P>

      <H2>7. Speicherdauer</H2>
      <P>Wir löschen nach festen Fristen. Technische Daten und nie bestätigte Registrierungen entfernt ein täglicher automatischer Lauf.</P>
      <UL>
        {RETENTION.map((r) => <li key={r}>{r}</li>)}
      </UL>

      <H2>8. Ihre Rechte</H2>
      <P>Sie haben das Recht auf Auskunft (Art. 15), Berichtigung (Art. 16), Löschung (Art. 17), Einschränkung der Verarbeitung (Art. 18), Datenübertragbarkeit (Art. 20) und Widerspruch (Art. 21 DSGVO) sowie das Recht, eine Einwilligung jederzeit zu widerrufen. Sie können sich bei einer Aufsichtsbehörde beschweren. Für uns zuständig ist das Bayerische Landesamt für Datenschutzaufsicht, Promenade 18, 91522 Ansbach.</P>
      <P><strong>Selbst erledigen:</strong> Hausverwaltungen laden unter Einstellungen → Datenschutz & AVV alle Daten als JSON-Datei herunter („Daten exportieren") und löschen dort ihr Konto mit allen Inhalten („Konto löschen"). Bewerter löschen ihre Bewertung über den Link in der Bestätigungsmail („Bewertung löschen"). Für alle anderen Anliegen genügt eine E-Mail an <A href={`mailto:${COMPANY.email}`}>{COMPANY.email}</A>. Wir antworten innerhalb eines Monats.</P>

      <H2>9. Cookies und Tracking</H2>
      <P>Immoverita setzt keine Tracking- oder Werbedienste ein und lädt keine Schriften oder Skripte von fremden Servern. Im Browser speichern wir nur, was technisch nötig ist: das Anmelde-Token nach dem Login sowie Ihre Eingaben im Einrichtungsassistenten (Local Storage) und die zufällige Sitzungskennung des Bewertungsformulars (Session Storage). Das ist nach § 25 Abs. 2 TDDDG ohne Einwilligung zulässig.</P>

      <H2>10. Pflicht zur Bereitstellung</H2>
      <P>Bewerter müssen keine personenbezogenen Daten angeben, die E-Mail-Adresse ist freiwillig. Hausverwaltungen benötigen eine E-Mail-Adresse für die Anmeldung.</P>
    </>
  );
}

function KiTransparenz() {
  return (
    <>
      <P>Immoverita setzt Künstliche Intelligenz im Bewertungsformular ein. Hier steht, wo, wofür, bei welchem Anbieter, mit welchen Daten und mit welchem Modell. Offenlegung nach Art. 13 DSGVO und Art. 50 der EU-KI-Verordnung.</P>
      <div className="overflow-x-auto my-4">
        <table className="w-full text-sm border border-border rounded-lg">
          <thead className="bg-muted/50">
            <tr>
              {['Wo', 'Wofür', 'Übermittelte Daten', 'Modell'].map((h) => (
                <th key={h} scope="col" className="text-left font-medium p-2.5 border-b border-border">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {AI_USES.map((row) => (
              <tr key={row[0]} className="align-top">
                {row.map((cell, i) => (
                  <td key={i} className={`p-2.5 border-b border-border ${i === 0 ? 'font-medium' : 'text-muted-foreground'} ${i === row.length - 1 ? 'whitespace-nowrap' : ''}`}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <P>Anbieter aller Funktionen ist OpenAI, L.L.C. (USA) über die Programmierschnittstelle. Es besteht ein Vertrag zur Auftragsverarbeitung, die Übermittlung stützt sich auf das EU-US Data Privacy Framework und Standardvertragsklauseln.</P>
      <H2>Was wir nie tun</H2>
      <UL>
        <li>Wir übermitteln keine E-Mail-Adressen an die KI.</li>
        <li>Die KI verändert keine Sterne und veröffentlicht nichts. Sie entscheiden selbst, ob Sie einen Vorschlag übernehmen und wo Sie ihn posten.</li>
        <li>Ihre Inhalte werden nicht zum Training von KI-Modellen verwendet.</li>
        <li>Es gibt keine automatisierten Entscheidungen mit rechtlicher Wirkung (Art. 22 DSGVO).</li>
      </UL>
      <H2>Ohne KI</H2>
      <P>Fällt der Dienst aus oder ist das tägliche Nutzungslimit erreicht, erhalten Sie stattdessen Vorschläge aus festen Textbausteinen. Das Formular funktioniert weiter.</P>
    </>
  );
}

function Barrierefreiheit() {
  return (
    <>
      <P>Wir möchten Immoverita für alle Menschen nutzbar machen und orientieren uns an den Web Content Accessibility Guidelines (WCAG) 2.2, Stufe AA, sowie am Barrierefreiheitsstärkungsgesetz (BFSG).</P>
      <H2>Stand der Vereinbarkeit</H2>
      <P>Immoverita ist teilweise mit WCAG 2.2 AA vereinbar. Eine vollständige Prüfung durch Dritte steht noch aus.</P>
      <H2>Bekannte Einschränkungen</H2>
      <UL>
        <li>Diagramme im Dashboard der Hausverwaltung (Verlauf, Klickfluss) sind für Screenreader nur eingeschränkt erfassbar.</li>
        <li>Die Sterne-Auswahl im Bewertungsformular ist noch nicht mit allen Screenreadern getestet.</li>
      </UL>
      <H2>Barriere melden</H2>
      <P>Ist Ihnen eine Barriere aufgefallen? Schreiben Sie an <A href={`mailto:${COMPANY.email}`}>{COMPANY.email}</A>. Wir antworten innerhalb von zwei Wochen.</P>
      <H2>Durchsetzung</H2>
      <P>Reagieren wir nicht zufriedenstellend, können Sie sich an die zuständige Marktüberwachungsbehörde der Länder für die Barrierefreiheit von Produkten und Dienstleistungen (MLBF) in Magdeburg wenden.</P>
    </>
  );
}

function Bildnachweise() {
  return (
    <>
      <UL>
        <li>Logo und Grafiken von Immoverita: eigene Gestaltung von Dwayne Advertising.</li>
        <li>Symbole: Lucide Icons, ISC-Lizenz (lucide.dev).</li>
        <li>Logos der Hausverwaltungen im Bewertungsformular werden von der jeweiligen Hausverwaltung hochgeladen, die die Rechte daran besitzt.</li>
      </UL>
      <P>Immoverita verwendet keine Stockfotos und keine KI-generierten Bilder.</P>
    </>
  );
}

function Agb() {
  return (
    <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
      Die Allgemeinen Geschäftsbedingungen für kostenpflichtige Pläne werden vor dem Start der Zahlungsfunktion hier veröffentlicht.
      Bis dahin ist die Nutzung kostenfrei. Fragen beantworten wir unter <A href={`mailto:${COMPANY.email}`}>{COMPANY.email}</A>.
    </div>
  );
}

const PAGES = {
  '/impressum':        { title: 'Impressum',                       law: 'Angaben gemäß § 5 DDG',                              Body: Impressum },
  '/datenschutz':      { title: 'Datenschutzerklärung',            law: 'Informationen nach Art. 13 und 14 DSGVO',            Body: Datenschutz },
  '/ki-transparenz':   { title: 'KI-Transparenz',                  law: 'Art. 13 DSGVO, Art. 50 KI-Verordnung',               Body: KiTransparenz },
  '/barrierefreiheit': { title: 'Erklärung zur Barrierefreiheit',  law: 'Barrierefreiheitsstärkungsgesetz (BFSG)',            Body: Barrierefreiheit },
  '/bildnachweise':    { title: 'Bildnachweise',                   law: 'Quellen und Lizenzen',                               Body: Bildnachweise },
  '/agb':              { title: 'Allgemeine Geschäftsbedingungen', law: 'Vertragsbedingungen für die Nutzung von Immoverita', Body: Agb },
};

export default function LegalPage() {
  const { pathname } = useLocation();
  const page = PAGES[pathname.replace(/\/$/, '')] || PAGES['/impressum'];
  const { Body } = page;

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="max-w-2xl mx-auto px-4 py-10 sm:py-16">
        <Link to="/" className="flex justify-center mb-8">
          <img src="/logo.png" alt="Immoverita – zur Startseite" className="h-14 w-auto" />
        </Link>
        <Card className="card-shadow">
          <CardContent className="pt-8 pb-8">
            <h1 className="text-2xl font-semibold tracking-tight">{page.title}</h1>
            <p className="text-sm text-muted-foreground mt-1 mb-4">{page.law} · Stand: {UPDATED}</p>

            <Body />

            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <a href={`mailto:${COMPANY.email}`} className="flex-1">
                <Button variant="secondary" className="w-full">
                  <Mail className="w-4 h-4 mr-2" />
                  {COMPANY.email}
                </Button>
              </a>
              <Link to="/" className="flex-1">
                <Button variant="ghost" className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Zur Startseite
                </Button>
              </Link>
            </div>

            <nav aria-label="Rechtliche Seiten" className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground pt-4 mt-6 border-t">
              {Object.entries(PAGES).map(([href, p]) => (
                <Link key={href} to={href} className={`hover:text-foreground ${href === pathname ? 'text-foreground font-medium' : ''}`}>{p.title}</Link>
              ))}
              <Link to="/kontakt" className="hover:text-foreground">Kontakt</Link>
            </nav>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
